import React, { useEffect, useState } from 'react'
import { ClientHeader } from './components/ClientHeader'
import { MetricsGrid } from './components/MetricsGrid'
import { RelationshipSnapshot } from './components/RelationshipSnapshot'
import { DataTrustPanel } from './components/DataTrustPanel'
import { UnifiedClientProfileData } from './types'
import './UnifiedClientProfile.css'

interface UnifiedClientProfileProps {
  clientId: string
}

export const UnifiedClientProfile: React.FC<UnifiedClientProfileProps> = ({ clientId }) => {
  const [data, setData] = useState<UnifiedClientProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchClientProfile = async () => {
      try {
        setLoading(true)
        setError(null)

        // TODO: Replace with actual API call
        // For now, using mock data based on converged data structure
        const mockData: UnifiedClientProfileData = {
          clientId: clientId,
          firstName: 'Emily',
          lastName: 'Davis',
          company: 'Tech Innovations LLC',
          designation: 'VP Portfolio Management',
          isActive: true,
          joiningSince: 'Client Since 2018',
          riskProfile: 'aggressive',
          advisorName: 'Sarah Johnson',

          metrics: {
            totalRelationshipValue: 8700000,
            investableAssets: 6200000,
            depositsOrCash: 1800000,
            lendingOrLiabilities: 650000
          },

          accounts: [
            {
              accountId: '1',
              accountName: 'Individual Brokerage',
              accountType: 'Investment',
              balance: 3245000,
              status: 'ACTIVE',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            },
            {
              accountId: '2',
              accountName: 'Joint Investment Account',
              accountType: 'Investment',
              balance: 2156000,
              status: 'ACTIVE',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          ],

          dataQuality: {
            trustScore: 94,
            completeness: 100,
            validationRate: 100,
            lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
          },

          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500))

        setData(mockData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch client profile')
      } finally {
        setLoading(false)
      }
    }

    fetchClientProfile()
  }, [clientId])

  const getNetWorthCategory = (totalValue: number): string => {
    if (totalValue >= 5000000) return 'High Net Worth'
    if (totalValue >= 1000000) return 'Medium Net Worth'
    return 'Standard'
  }

  if (loading) {
    return (
      <div className="unified-client-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading client profile...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="unified-client-profile-error">
        <p>Error: {error}</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="unified-client-profile-empty">
        <p>No client profile data available</p>
      </div>
    )
  }

  const fullName = `${data.firstName} ${data.lastName}`
  const netWorthCategory = getNetWorthCategory(data.metrics.totalRelationshipValue)

  return (
    <div className="unified-client-profile">
      <ClientHeader
        fullName={fullName}
        netWorthCategory={netWorthCategory}
        advisorName={data.advisorName}
        clientSince={data.joiningSince}
        isActive={data.isActive}
      />

      <MetricsGrid
        investableAssets={data.metrics.investableAssets}
        depositsOrCash={data.metrics.depositsOrCash}
        lendingOrLiabilities={data.metrics.lendingOrLiabilities}
        trustScore={data.dataQuality.trustScore}
      />

      <div className="content-grid">
        <RelationshipSnapshot accounts={data.accounts} />
        <DataTrustPanel
          trustScore={data.dataQuality.trustScore}
          lastUpdated={data.dataQuality.lastUpdated}
        />
      </div>
    </div>
  )
}
